import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "du-one-year-pg-pse-priority-high-court-2026",
  title: "DU's One-Year PG Fight Reaches the High Court: The P1 to P4 Priority System, Explained",
  description:
    "About 100 students have challenged Delhi University's one-year master's admission rules in the Delhi High Court. What the P1 to P4 priority system is, why programme and general graduates are upset, and what it means for your own plans.",
  pubDate: "2026-10-26",
  updatedDate: "2026-10-26",
  authorSlug: "dhairya-madaan",
  category: "news",
  tags: ["DU PG admission", "one-year PG", "Delhi High Court", "four-year UG", "PSE priority", "DU news"],
  readMinutes: 8,
  heroEyebrow: "News · Admissions",
};

const src = (href: string, label: string) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {label}
  </a>
);

export function Body() {
  return (
    <>
      <figure className="my-8">
        <img
          src="/blog/du-one-year-pg-pse-priority-high-court-2026.jpg"
          alt="A student holding an admission form outside a court building in Delhi."
          width={1200}
          height={688}
          fetchPriority="high"
          className="w-full h-auto border border-rule bg-card"
        />
        <figcaption className="mt-3 mono-label text-pencil text-center">
          Illustration. Not a photo of the actual hearing.
        </figcaption>
      </figure>

      <div className="border border-rule bg-card p-5 my-6 text-sm">
        <strong className="mono-label text-grade">How we sourced this</strong>
        <p className="mt-2">
          The facts about the petitions come from{" "}
          {src("https://www.newindianexpress.com/cities/delhi/2026/Oct/17/100-petitioners-knock-hc-door-over-dus-1-yr-postgraduate-programme", "The New Indian Express (17 October 2026)")}
          . The direct PhD details come from{" "}
          {src("https://dubeat.com/2026/10/18/direct-phd-admissions-du-entrance-test/", "DU Beat")}{" "}
          and{" "}
          {src("https://news.careers360.com/delhi-university-phd-admissions-entrance-test-gets-70-weightage-interview-30-check-syllabus", "Careers360")}
          . The case is still in court. We do not predict the outcome, and nothing here is legal advice.
        </p>
      </div>

      <p>
        When DU moved to the four-year undergraduate programme under UGCF 2022, the promise was simple: do four years and you can finish a master's in one. The first batch has now reached that point, and the reality is messier. Around 100 students have gone to the Delhi High Court to challenge how DU decides who gets a seat in its new one-year PG programmes for 2026-27.
      </p>
      <p>
        If you are in a three-year degree, a BA Programme, or planning which fourth-year track to pick, this affects you. Here is what is going on, in plain language.
      </p>

      <h2>First, how the one-year PG works</h2>
      <p>
        Under the new structure, students who complete a four-year honours degree can do a one-year master's. Students with a three-year degree still go through the traditional two-year route. For the one-year programmes, DU uses something called <strong>programme-specific eligibility (PSE)</strong>, which sorts applicants into priority levels.
      </p>

      <h2>The P1 to P4 system</h2>
      <p>
        As reported by The New Indian Express, candidates are placed in four priority levels, P1 to P4:
      </p>
      <ul>
        <li><strong>P1:</strong> candidates whose undergraduate degree is considered directly relevant to the master's programme. They get first preference.</li>
        <li><strong>P2:</strong> candidates from other eligible undergraduate backgrounds, considered after P1, if seats remain.</li>
        <li><strong>P3 and P4:</strong> lower levels considered after that, again subject to seats.</li>
      </ul>
      <p>
        In practice, this means a seat goes to a higher priority level before a lower one is even looked at. If P1 fills most of a course, a P2 or P3 student with excellent marks may never get a chance.
      </p>

      <h2>What the students are challenging</h2>
      <p>
        The petitions question two things: the eligibility criteria, and the priority system itself. The main complaint is that graduates with <strong>four-year honours degrees are preferred over those who did general or programme undergraduate courses</strong>. Students say this leaves eligible applicants unsure whether they will get in at all.
      </p>
      <p>
        One petition, filed on 16 September, raised a related problem: seats in the university going unfilled because of the honours preference. Students had also raised concerns about admission priorities, transparency and seat allocation with the Vice-Chancellor before going to court.
      </p>
      <p>
        That second point is worth sitting with. If seats really are going empty while eligible students are turned away, it is a problem for both sides, and it is the kind of thing courts tend to look at closely.
      </p>

      <h2>Both sides, fairly</h2>
      <p>
        It helps to understand why DU might have designed it this way. A one-year master's assumes you already covered much of the ground in your fourth year. Giving first preference to students whose degree matches the subject is one way of making sure the one-year course does not have to re-teach basics. That is the argument for the system.
      </p>
      <p>
        The argument against it is about fairness and timing. Many students chose their degree before the full rules for the one-year PG were clear. A BA Programme student who later fell in love with a subject now finds themselves at the back of the queue, whatever their marks. And if seats stay empty, the system is not even serving the university well.
      </p>
      <p>
        Which side the court agrees with, if either, is not something we can know yet.
      </p>

      <h2>What this means for you, depending on your year</h2>
      <h3>If you are in your first or second year</h3>
      <p>
        Your choices now matter more than they used to. Before you decide whether to do a fourth year, and in what, check which PG courses you might want and which undergraduate backgrounds count as directly relevant for them. Our guide to <a href="/blog/du-ba-economics-hons-vs-ba-programme-2026" className="font-semibold underline text-grade">BA Economics Honours vs BA Programme</a> explains the practical differences.
      </p>
      <h3>If you are in your third year</h3>
      <p>
        Keep both doors open. Prepare for the two-year PG route and CUET PG at the same time as you consider a fourth year. Do not assume a one-year seat will be there. If economics is your field, our <a href="/blog/cuet-pg-economics-2026-cutoff-dse-jnu" className="font-semibold underline text-grade">CUET PG Economics guide</a> covers the main options.
      </p>
      <h3>If you applied for 2026-27 and are affected</h3>
      <p>
        Keep copies of everything: your application, priority category, any communication with the university and the grievance committee. Follow official DU notices and your college's notices closely. If you are thinking of legal action yourself, speak to a lawyer. We cannot advise on that.
      </p>

      <h2>A related change: direct PhD after four years</h2>
      <p>
        In the same week, DU released the pattern for its entrance test for direct PhD admission after a four-year UG with research or entrepreneurship. According to DU Beat and Careers360:
      </p>
      <ul>
        <li>You need at least 7.5 CGPA (7 for SC, ST and PwBD candidates).</li>
        <li>You must have done a dissertation, an academic project (not translation work) or entrepreneurship in your fourth year.</li>
        <li>It is only for those who have not qualified UGC-CSIR NET, GATE, CEED or a similar national test.</li>
        <li>The test is two hours and 100 marks: 50 marks of research methodology MCQs with negative marking, and 50 marks of descriptive questions on your own fourth-year work.</li>
        <li>Careers360 reports the entrance test carries 70% weight and the interview 30%.</li>
      </ul>
      <p>
        Put together, the message from DU is clear: the fourth year is becoming the gateway to faster postgraduate and research routes. Whether the priority rules for those routes are fair is now a question for the court.
      </p>

      <h2>The bottom line</h2>
      <p>
        The one-year PG is a new system, and the first batch is finding its rough edges. If you are early in your degree, plan your fourth year with the PG priority rules in mind. If you are late in your degree, do not bet everything on a one-year seat. We will update this article when the High Court rules or DU changes its policy.
      </p>
      <p>
        For background on the research track, see our explainer on <a href="/blog/du-direct-phd-four-year-ug" className="font-semibold underline text-grade">direct PhD after the four-year UG</a>.
      </p>
    </>
  );
}
